import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth, RoleSwitcherProvider, useRoleSwitcher } from './features/auth';
import { ThemeProvider } from './hooks/useTheme.jsx';
import { PrivacyMaskProvider } from './contexts/PrivacyMaskContext';
import { SchoolProvider } from './contexts/SchoolContext';
import { ScopeProvider } from './contexts/ScopeContext';
import { ContextModeProvider } from './contexts/ContextModeContext';
import { MenuItemsProvider, useMenuItemsContext } from './contexts/MenuItemsContext';
import { NavBar, LoadingSpinner, ErrorBoundary } from './components/shared/UIComponents';
import AnnouncementBanner from './components/announcements/AnnouncementBanner';
import CampaignBanner from './features/campaigns/components/CampaignBanner';
import { NotificationProvider } from './components/notifications/NotificationSystem';
import { ToastProvider } from './components/feedback/ToastSystem';
import UpdateNotification from './components/notifications/UpdateNotification';
import VersionDebugPanel from './components/debug/VersionDebugPanel';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import LoadingOrchestrator from './components/LoadingOrchestrator';
import EnvironmentBadge from './components/layout/EnvironmentBadge';
import SchoolBrandingController from './features/branding/SchoolBrandingController';
import { BrandingProvider } from './features/branding/BrandingProvider';
import QuickIssueSkeleton from './components/skeletons/QuickIssueSkeleton';
import StudentsManagerSkeleton from './components/skeletons/StudentsManagerSkeleton';
import SmartListsSkeleton from './components/skeletons/SmartListsSkeleton';
import SeatingManagerSkeleton from './components/skeletons/SeatingManagerSkeleton';
import LinkingPageSkeleton from './components/parentLinking/LinkingPageSkeleton';
import SchoolInvitationModal from './features/auth/components/SchoolInvitationModal';
import GlobalSessionFooter from './components/session/GlobalSessionFooter';
import { AgentChat, AgentChatButton } from './features/agent';
import { ROUTES } from './utils/constants';
import { dashboardPathForRole } from './routes/dashboardRouteContract';
import {
  LEGACY_DOMAIN_EDITOR_ROUTES,
  buildDomainEditorDestination,
  setupBehaviourDestination,
} from './features/domains/utils/domainRouteCompatibility';
import { getIconByName } from './utils/iconMapping';
import { MobileDock, PageShell, PersistentContextBar, SubstrateRail, SubstratePanel } from './components/layout';
import { DomainProvider } from './features/domains/components/DomainProvider';
import useSubstratePanelStore from '@/stores/substratePanelStore';
import useParentStore from './stores/parentStore';
import { shouldRedirectUnlinkedParent } from './components/menu/parentNavigation';
import DeveloperWorkspaceShell from './pages/developer/DeveloperWorkspaceShell';
import { isDeveloperWorkspaceAvailable } from './config/developerWorkspace';
// Tours temporarily disabled — code preserved in src/features/tours for later polish.
// import { TourProvider } from './features/tours';
import { useSchool } from './contexts/SchoolContext';
import { clearGraduatedStudents } from './scripts/clearGraduatedStudents';
import { clearGraduatedSimple, clearGraduatedConfirm } from './scripts/clearGraduatedStudentsSimple';
import { inspectStudentClasses } from './scripts/inspectStudentClasses';
import { reactivateGrade7, reactivateGrade7Confirm } from './scripts/reactivateCurrentStudents';
import { fixStudentStatus, fixStudentStatusConfirm } from './scripts/fixStudentStatus';
import { backfillGrades } from './scripts/backfillGrades';
import { diagnoseParentLinks } from './scripts/diagnoseParentLinks';
import { createTestMention } from './scripts/createTestMention';
import { monitorMessagingSystem } from './scripts/monitorMessagingSystem';
import { checkTemplates } from './scripts/checkTemplates';
import diagnoseConsequenceMessages from './scripts/diagnoseConsequenceMessages';
import { addParentFlag } from './scripts/addParentFlag';
import { addLinkedStudentIds } from './scripts/addLinkedStudentIds';
import { migrateParentSchoolAssignments, checkParentData } from './scripts/migrateParentSchoolAssignments';
import { testParentSchoolAssignment } from './scripts/testParentSchoolAssignment';
import { migrateParentUsers } from './scripts/migrateParentUsers';
import { checkSchoolsData, addSchoolIdsArray } from './scripts/checkSchoolsData';
import { analyzeParentData } from './scripts/analyzeParentData';
import { checkRecentTestMessages } from './scripts/checkRecentTestMessages';
import { debugTestMessages } from './scripts/debugTestMessages';
import { backfillCleanWeekRecognitions } from './scripts/backfillCleanWeekRecognitions';
import { fixCleanWeekRecognitions } from './scripts/fixCleanWeekRecognitions';
import { importFamilyCards } from './scripts/importFamilyCards';
import { sendFamilyBroadcast } from './scripts/sendFamilyBroadcast';
import { seedRolesTeamsMenu } from './scripts/seedRolesTeamsMenu';
import { seedZwaanswykOffences } from './scripts/seedZwaanswykOffences';
import { setAdjudicationTypes } from './scripts/setAdjudicationTypes';
import { backfillHousePoints } from './scripts/backfillHousePoints';

// Import transition manager for debugging
import { transitionManager } from './services/discipline/core/TransitionManager';

// Agent bridge — exposes services on window.swan for AI agents & console
import './utils/agentBridge';

// Chunk loading error handler - forces reload when lazy loaded chunks fail
// This happens after deployments when cached code tries to load non-existent chunks
const handleChunkLoadError = (error) => {
  const isChunkLoadError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.name === 'ChunkLoadError' ||
    error?.stack?.includes('dynamically imported');

  if (isChunkLoadError) {
    // SAFETY: Prevent infinite reload loops
    const reloadKey = 'swan-last-chunk-reload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    // Only reload once per 10 seconds
    if (lastReload && (now - parseInt(lastReload)) < 10000) {
      console.error('️ Chunk reload loop detected - aborting auto-reload. Please manually refresh.');
      return false;
    }

    console.warn(' Chunk load error detected - forcing reload to fetch latest version');
    sessionStorage.setItem(reloadKey, now.toString());

    // Clear service worker caches and reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    // Use location.reload(true) to force bypass cache
    window.location.reload();
    return true;
  }
  return false;
};

// Listen for errors during module loading
window.addEventListener('error', (event) => {
  if (handleChunkLoadError(event.error || event.reason)) {
    event.preventDefault();
  }
});

// Lazy-loaded chunks fail as PROMISE REJECTIONS, not 'error' events. After a deploy,
// the auto-update reload can briefly try to load a stale chunk — route those rejections
// through the same recovery so the page self-heals instead of going blank. The 10s loop
// guard inside handleChunkLoadError prevents reload storms. Non-chunk rejections are
// swallowed as before to keep the console clean.
window.addEventListener('unhandledrejection', (event) => {
  if (handleChunkLoadError(event.reason)) {
    event.preventDefault();
    return;
  }
  console.warn('Unhandled Promise Rejection:', event.reason);
  event.preventDefault();
});

const shouldAutoRegisterDevTools = () => {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return false;
  return window.localStorage.getItem('swan_enable_dev_tools') === 'true';
};

// CLEAN DEVELOPMENT SETUP - No auto-loading scripts to prevent aggressive reloads
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  // On-demand loader for development tools when needed
  window.loadDevTools = async (category) => {
    switch(category) {
      case 'migration':
        await import('./utils/migrationTestSuite');
        await import('./utils/runCompleteMigration');
        await import('./utils/quickValidation');
        break;
      case 'testing':
        await import('./tests/rules/zero-tolerance/zero-tolerance-browser-module');
        await import('./tests/rules/mention/mention-browser-module');
        await import('./tests/rules/threshold/threshold-browser-module');
        await import('./tests/rules/failure-recovery-browser-module');
        await import('./utils/testNotifications');
        await import('./utils/testPresetMessages');
        await import('./utils/testConversations');
        await import('./scripts/createTestRecognition');
        await import('./scripts/testConsequenceTrigger');
        await import('./scripts/checkMessageFlowState');
        await import('./scripts/createTestConsequence');
        await import('./scripts/testRightNow');
        await import('./scripts/directTest');
        break;
      case 'debug':
        await import('./utils/debugConsequenceTypes');
        await import('./utils/testCloudFunctions');
        await import('./utils/checkSchoolIds');
        await import('./utils/bustCache');
        break;
      case 'cache':
        await import('./utils/bustCache');
        break;
      case 'seeding':
        await import('./utils/seedConsequenceTypes');
        await import('./utils/seedStickerPresets');
        await import('./utils/seedSchoolDatePeriods');
        await import('./utils/seedAcademicTimeframes');
        break;
      case 'validation':
        await import('./utils/migrationTestSuite');
        await import('./utils/quickValidation');
        break;
      case 'student':
        await import('./scripts/clearGraduatedStudents');
        await import('./scripts/clearGraduatedStudentsSimple');
        await import('./scripts/inspectStudentClasses');
        await import('./scripts/reactivateCurrentStudents');
        await import('./scripts/fixStudentStatus');
        await import('./scripts/fixPointsSchoolId');
        // await import('./scripts/clearStudentStatus'); // File doesn't exist
        // await import('./scripts/migrateStudentStatus'); // File doesn't exist
        // await import('./scripts/assignClassOrder'); // File doesn't exist
        break;
      case 'migration-scripts':
        await import('./scripts/migrateMentions');
        await import('./scripts/migrateMentionTypesToHierarchical').then(module => {
          window.migrateMentionTypes = module.migrateMentionTypes;
          console.log(' migrateMentionTypes loaded - usage: await window.migrateMentionTypes(schoolId, { dryRun: true })');
        });
        await import('./scripts/migrateOffenseMentionTypes');
        await import('./scripts/migrateRecognitions');
        await import('./scripts/validateMigration');
        await import('./scripts/migrateCategories');
        await import('./scripts/checkCategories');
        await import('./scripts/testCategoriesDirectly');
        await import('./scripts/checkDetentionCounters');
        await import('./scripts/backfillEscalationRuleTypeIds');
        // await import('./scripts/fixClassYearFields'); // File doesn't exist
        break;
      case 'teams':
        await import('./scripts/addTeamsMenuItem');
        break;
      case 'domains':
        await import('./scripts/addDomainsMenuItem');
        break;
      case 'category-testing':
        await import('./scripts/testCategoryRead');
        await import('./scripts/testCategoryQueryWithConstraint');
        await import('./scripts/testValueQueryWithConstraint');
        break;
      case 'test-data':
        await import('./utils/purgeHarryTestData');
        break;
      case 'browser-scripts':
        await import('./utils/browserScripts/inspectClasses');
        await import('./utils/browserScripts/updateClassesActive');
        await import('./utils/browserScripts/migrateClassesActiveYear');
        await import('./utils/browserScripts/debugSmartLists');
        await import('./utils/browserScripts/compareCollections');
        await import('./utils/browserScripts/analyzeStudentDuplicates');
        await import('./utils/browserScripts/clearGraduatedClassIds');
        await import('./utils/browserScripts/debugSequentialCounts').then(module => {
          window.debugSequentialCounts = module.debugSequentialCounts;
          console.log(' debugSequentialCounts available - usage: debugSequentialCounts(studentId, schoolId)');
        });
        await import('./utils/browserScripts/checkQueueSequentialNumbers').then(module => {
          window.checkQueueSequentialNumbers = module.checkQueueSequentialNumbers;
          console.log(' checkQueueSequentialNumbers available - usage: checkQueueSequentialNumbers(schoolId, limitCount)');
        });
        await import("./utils/browserScripts/checkMentionTypes").then(module => {
          window.checkMentionTypes = module.checkMentionTypes;
          console.log(" checkMentionTypes available - usage: await checkMentionTypes()");
        });
        await import("./utils/browserScripts/seedStickersForSchool").then(module => {
          window.seedStickersForSchool = module.seedStickersForSchool;
          console.log(" seedStickersForSchool available - usage: await seedStickersForSchool()");
        });
        break;
      default:
        console.log('Available categories: migration, testing, debug, cache, seeding, validation, test-data, student, migration-scripts, teams, category-testing, browser-scripts');
    }
  };
}

if (shouldAutoRegisterDevTools()) {
  import('./tests/rules/zero-tolerance/zero-tolerance-browser-module').catch((error) => {
    console.warn('️ Failed to load zero-tolerance-browser-module:', error.message);
  }).then((module) => {
    if (module) {
      window.ZeroToleranceTestModule = module.default;
      window.runZeroToleranceModuleTest = async function() {
        const test = new module.default();
        return await test.runTest();
      };
    }
  });

  import('./tests/rules/mention/mention-browser-module').catch((error) => {
    console.warn('️ Failed to load mention-browser-module:', error.message);
  }).then((module) => {
    if (module) {
      window.MentionTestModule = module.default;
      window.runMentionModuleTest = async function() {
        const test = new module.default();
        return await test.runAllTests();
      };
    }
  });

  import('./tests/rules/threshold/threshold-browser-module').catch((error) => {
    console.warn('️ Failed to load threshold-browser-module:', error.message);
  }).then((module) => {
    if (module) {
      window.ThresholdTestModule = module.default;
      window.runThresholdModuleTest = async function() {
        const test = new module.default();
        return await test.runTest();
      };
    }
  });

  import('./utils/runCompleteMigration');
  import('./utils/quickValidation');

  // Register migration test suite
  import('./utils/migrationTestSuite').catch((error) => {
    console.warn('️ Failed to load migrationTestSuite:', error.message);
  }).then((module) => {
    if (module && !window.runMigrationTests) {
      window.runMigrationTests = module.runMigrationTests;
      window.quickValidation = module.quickValidation;
      window.migrationReport = module.migrationReport;
      window.testCounters = module.testCounters;
    }
  });

  // Register consequence types seeding script
  import('./utils/seedConsequenceTypes').catch((error) => {
    console.warn('️ Failed to load seedConsequenceTypes:', error.message);
  }).then((module) => {
    if (module && !window.seedConsequenceTypes) {
      window.seedConsequenceTypes = module.seedConsequenceTypes;
      window.consequenceTypesExist = module.consequenceTypesExist;
    }
  });

  // Register sticker presets seeding script
  import('./utils/seedStickerPresets').catch((error) => {
    console.warn('️ Failed to load seedStickerPresets:', error.message);
  }).then((module) => {
    if (module && !window.seedStickerPresets) {
      window.seedStickerPresets = module.seedStickerPresets;
      window.stickerPresetsExist = module.stickerPresetsExist;
    }
  });

  // DISABLED - These scripts were causing reload issues and are now on-demand
  // All development tools available via: window.loadDevTools("category")
  console.log(' Clean development setup complete');

  // Register transition manager debug utilities
  if (!window.transitionManager) {
    window.transitionManager = transitionManager;
    window.transitionStatus = () => console.table(transitionManager.getStatus());
    window.enableCloudCounters = () => transitionManager.setFlag('useCloudSequentialCounters', true);
    window.enableCloudRules = () => transitionManager.setFlag('useCloudRuleEvaluation', true);
    window.enableComparison = () => transitionManager.setFlag('enableComparisonMode', true);
    window.disableCloudFeatures = () => transitionManager.disableCloudFeatures();
    window.enableCloudFeatures = () => transitionManager.enableCloudFeatures();
  }

  // Simple debug function for user permissions
  console.log(' Registering debugUserPermissions function...');
  if (!window.debugUserPermissions) {
    console.log(' Creating debugUserPermissions function...');
    window.debugUserPermissions = async function() {
      try {
        console.log(' Debugging user permissions...');
        
        // Check localStorage for context
        const schoolContext = localStorage.getItem('schoolContext');
        if (schoolContext) {
          const parsed = JSON.parse(schoolContext);
          console.log('School context from localStorage:', parsed);
          console.log('Current schoolId:', parsed.schoolId);
        }
        
        const authCache = localStorage.getItem('authCache');
        if (authCache) {
          const authParsed = JSON.parse(authCache);
          console.log('Auth cache:', authParsed);
        }
        
        // Try to get Firebase instances from the window
        if (window.firebase && window.firebase.auth) {
          const currentUser = window.firebase.auth.currentUser;
          if (currentUser) {
            console.log('Current user:', currentUser.uid);
            console.log('Current user email:', currentUser.email);
            
            // Try to fetch user document
            if (window.firebase.db) {
              const { doc, getDoc } = window.firebase.firestore;
              const userDoc = await getDoc(doc(window.firebase.db, 'users', currentUser.uid));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log('User data:', userData);
                console.log('User schoolId:', userData.schoolId);
                console.log('User assignedSchoolId:', userData.assignedSchoolId);
              } else {
                console.error('User document not found');
              }
            }
          } else {
            console.error('No authenticated user found');
          }
        } else {
          console.log('Firebase not directly available on window. Check logs above for context.');
        }
        
      } catch (error) {
        console.error('Error debugging permissions:', error);
      }
    };
    
    console.log(' User permissions debug function loaded!');
    console.log('Run with: await window.debugUserPermissions()');
  } else {
    console.log('ℹ️ debugUserPermissions already exists');
  }

  // Register cloud functions test script
  import('./utils/testCloudFunctions').catch((error) => {
    console.warn('️ Failed to load testCloudFunctions:', error.message);
  }).then((module) => {
    if (module && !window.testCloudFunctions) {
      window.testCloudFunctions = module.testCloudFunctions;
    }
  });



  // Register debug consequence types script
  import('./utils/debugConsequenceTypes').catch((error) => {
    console.warn('️ Failed to load debugConsequenceTypes:', error.message);
  }).then((module) => {
    if (module && !window.debugConsequenceTypes) {
      window.debugConsequenceTypes = module.debugConsequenceTypes;
      window.flushAndReload = module.flushAndReload;
    }
  });

  // Register cache buster
  import('./utils/bustCache').catch((error) => {
    console.warn('️ Failed to load bustCache:', error.message);
  }).then((module) => {
    if (module && !window.bustConsequenceTypesCache) {
      window.bustConsequenceTypesCache = module.bustConsequenceTypesCache;
    }
  });

  // Register clear graduated students script
  if (!window.clearGraduatedStudents) {
    window.clearGraduatedStudents = clearGraduatedStudents;
  }

  // Register simpler version that handles userId automatically
  if (!window.clearGraduatedSimple) {
    window.clearGraduatedSimple = clearGraduatedSimple;
    window.clearGraduatedConfirm = clearGraduatedConfirm;
  }

  // Register inspection script
  if (!window.inspectStudentClasses) {
    window.inspectStudentClasses = inspectStudentClasses;
  }

  // Register reactivation scripts
  if (!window.reactivateGrade7) {
    window.reactivateGrade7 = reactivateGrade7;
    window.reactivateGrade7Confirm = reactivateGrade7Confirm;
  }

  // Register status fix scripts
  if (!window.fixStudentStatus) {
    window.fixStudentStatus = fixStudentStatus;
    window.fixStudentStatusConfirm = fixStudentStatusConfirm;
  }

  // Grade backfill migration
  window.backfillGrades = backfillGrades;

  // Register parent link diagnostic
  if (!window.diagnoseParentLinks) {
    window.diagnoseParentLinks = diagnoseParentLinks;
    window.createTestMention = createTestMention;
    window.monitorMessagingSystem = monitorMessagingSystem;
    window.checkTemplates = checkTemplates;
    window.diagnoseConsequenceMessages = diagnoseConsequenceMessages;
    window.addParentFlag = addParentFlag;
    window.addLinkedStudentIds = addLinkedStudentIds;
    window.migrateParentSchoolAssignments = migrateParentSchoolAssignments;
    window.checkParentData = checkParentData;
    window.testParentSchoolAssignment = testParentSchoolAssignment;
  }

  // Register parent user migration script (always register to ensure availability)
  if (!window.migrateParentUsers) {
    window.migrateParentUsers = migrateParentUsers;
    console.log(' Migration script loaded. Run: await window.migrateParentUsers()');
  }

  // Register school data diagnostic scripts
  if (!window.checkSchoolsData) {
    window.checkSchoolsData = checkSchoolsData;
    window.addSchoolIdsArray = addSchoolIdsArray;
    console.log(' School diagnostic scripts loaded. Run: await window.checkSchoolsData()');
  }

  // Register parent data analysis script
  if (!window.analyzeParentData) {
    window.analyzeParentData = analyzeParentData;
    console.log(' Parent analysis script loaded. Run: await window.analyzeParentData()');
  }

  // Register clean week recognition backfill script
  if (!window.backfillCleanWeekRecognitions) {
    window.backfillCleanWeekRecognitions = backfillCleanWeekRecognitions;
    console.log(' Clean Week backfill loaded. Run: await window.backfillCleanWeekRecognitions()');
  }

  // Register clean week recognition fix script
  if (!window.fixCleanWeekRecognitions) {
    window.fixCleanWeekRecognitions = fixCleanWeekRecognitions;
    console.log(' Clean Week fix loaded. Run: await window.fixCleanWeekRecognitions()');
  }

  // Register family cards import script
  if (!window.importFamilyCards) {
    window.importFamilyCards = importFamilyCards;
    console.log(' Family Cards import loaded. Run: await window.importFamilyCards("csv\\tdata", { dryRun: true })');
  }

  // Register family broadcast script
  if (!window.sendFamilyBroadcast) {
    window.sendFamilyBroadcast = sendFamilyBroadcast;
    console.log(' Family Broadcast loaded. Run: await window.sendFamilyBroadcast({ subject: "...", body: "..." })');
  }

  // Register Zwaanswyk offences seed script
  if (!window.seedZwaanswykOffences) {
    window.seedZwaanswykOffences = seedZwaanswykOffences;
  }

  // Set requiresAdjudication on serious consequence types
  if (!window.setAdjudicationTypes) {
    window.setAdjudicationTypes = setAdjudicationTypes;
  }

  // Backfill house points from recognitions/mentions
  if (!window.backfillHousePoints) {
    window.backfillHousePoints = backfillHousePoints;
  }

  // Register profile route permissions script
  import('./scripts/addProfileRoutePermissions').then((module) => {
    if (!window.addProfileRoutePermissions) {
      window.addProfileRoutePermissions = module.addProfileRoutePermissions;
    }
  });

  // Register message history menu item script
  import('./scripts/addMessageHistoryMenuItem').then((module) => {
    if (!window.addMessageHistoryMenuItem) {
      window.addMessageHistoryMenuItem = module.addMessageHistoryMenuItem;
      console.log(' Message History menu script loaded. Run: await window.addMessageHistoryMenuItem()');
    }
  });

  // Register smart messages menu item script
  import('./scripts/addSmartMessagesMenuItem').then((module) => {
    if (!window.addSmartMessagesMenuItem) {
      window.addSmartMessagesMenuItem = module.addSmartMessagesMenuItem;
      console.log(' Smart Messages menu script loaded. Run: await window.addSmartMessagesMenuItem()');
    }
  });

  // Issue #129: setup menu restructure
  import('./scripts/restructureSetupMenu').then((module) => {
    if (!window.restructureSetupMenu) {
      window.restructureSetupMenu = module.restructureSetupMenu;
      console.log(' Setup menu restructure script loaded. Run: await window.restructureSetupMenu()');
    }
  });

  // Phase 0 audit — read-only menu state inventory
  import('./scripts/auditMenuState').then((module) => {
    if (!window.auditMenuState) {
      window.auditMenuState = module.auditMenuState;
      console.log(' Menu audit script loaded. Run: await window.auditMenuState()');
    }
  });

  // Phase 1 — seed top-level People entry (additive, idempotent)
  import('./scripts/seedPeopleMenuItem').then((module) => {
    if (!window.seedPeopleMenuItem) {
      window.seedPeopleMenuItem = module.seedPeopleMenuItem;
      console.log(' People menu seed script loaded. Run: await window.seedPeopleMenuItem()');
    }
  });

  // Register domains menu item script
  import('./scripts/addDomainsMenuItem').then((module) => {
    if (!window.addDomainsMenuItem) {
      window.addDomainsMenuItem = module.addDomainsMenuItem;
      console.log(' Domains menu script loaded. Run: await window.addDomainsMenuItem()');
    }
  });

  // Register stuck messages diagnostic script
  import('./scripts/diagnoseStuckMessages').then((module) => {
    if (!window.diagnoseStuckMessages) {
      window.diagnoseStuckMessages = module.diagnoseStuckMessages;
      console.log(' Stuck messages diagnostic loaded. Run: await window.diagnoseStuckMessages()');
    }
  });

  // Register debug sequential counts script
  import('../debug-sequential-counts').catch((error) => {
    console.warn('️ Failed to load debug-sequential-counts:', error.message);
  });

  // Import linked student IDs script for parent access
  import('./utils/browserScripts/addLinkedStudentIds').then(() => {
    console.log(' Script loaded. Run: await window.addLinkedStudentIds()');
  });

  // Import migration script for parent linkedStudentIds
  import('./utils/browserScripts/migrateParentLinkedStudentIds').then(() => {
    console.log(' Migration script loaded. Run: await window.migrateParentLinkedStudentIds()');
  });

  // Bootstrap development environment script
  import('./utils/browserScripts/bootstrapDevEnvironment').then((module) => {
    if (!window.bootstrapDevEnvironment) {
      window.bootstrapDevEnvironment = module.bootstrapDevEnvironment;
      console.log(' Bootstrap script loaded. Run: await window.bootstrapDevEnvironment()');
    }
  });

  // Export/Import menuItems (for copying prod → dev)
  import('./utils/browserScripts/exportMenuItems').then((module) => {
    if (!window.exportMenuItems) {
      window.exportMenuItems = module.exportMenuItems;
      window.importMenuItems = module.importMenuItems;
      console.log(' Menu export/import loaded. Run: await window.exportMenuItems() in PROD, then await window.importMenuItems(data) in DEV');
    }
  });

  // Setup messaging test data
  import('./utils/browserScripts/setupMessagingTest').then((module) => {
    if (!window.setupMessagingTest) {
      window.setupMessagingTest = module.setupMessagingTest;
      console.log(' Messaging test setup loaded. Run: await window.setupMessagingTest()');
    }
  });

  // Test email delivery
  import('./utils/browserScripts/testEmail').then((module) => {
    if (!window.testEmail) {
      window.testEmail = module.testEmail;
      console.log(' Email test loaded. Run: await window.testEmail("your-email@example.com")');
    }
  });

  // Clear students cache (for hierarchical migration)
  import('./utils/browserScripts/clearStudentsCache').then((module) => {
    if (!window.clearStudentsCache) {
      window.clearStudentsCache = module.clearStudentsCache;
      window.clearAllSwanCache = module.clearAllSwanCache;
      console.log(' Cache clearing loaded. Run: window.clearStudentsCache() or window.clearAllSwanCache()');
    }
  });

  // Test Telegram connections for parent
  import('./utils/browserScripts/testTelegramConnections').then((module) => {
    if (!window.testTelegramConnections) {
      window.testTelegramConnections = module.testTelegramConnections;
      console.log(' Telegram test loaded. Run: await window.testTelegramConnections(currentUser)');
    }
  });

  // Diagnose parent links query issues
  import('../public/diagnose-parent-links-query').then(() => {
    console.log(' Parent links query diagnostic loaded. Run: await window.diagnoseParentLinksQuery()');
  }).catch((err) => {
    console.warn('️ Failed to load parent links diagnostic:', err.message);
  });

  // Import class inspection script
  import('./utils/browserScripts/inspectClasses').then(() => {
    console.log(' Class inspection script loaded. Use window.inspectClasses() to analyze classes.');
  });

  // Import class update script
  import('./utils/browserScripts/updateClassesActive').then(() => {
    console.log(' Class update script loaded. Use window.updateClassesActive() to mark classes as active/archived.');
  });

  // Import class migration script
  import('./utils/browserScripts/migrateClassesActiveYear').then(() => {
    console.log(' Class migration script loaded. Use window.migrateClassesActiveYear() to add active and academicYear fields.');
  });

  // Import debug script
  import('./utils/browserScripts/debugSmartLists').then(() => {
    console.log(' Debug script loaded. Use window.debugSmartLists() to see what data SmartLists is getting.');
  });

  // Import compare collections script
  import('./utils/browserScripts/compareCollections').then(() => {
    console.log(' Compare script loaded. Use window.compareCollections() to compare flat vs hierarchical data.');
  });

  // Import analyze duplicates script
  import('./utils/browserScripts/analyzeStudentDuplicates').then(() => {
    console.log('�� Analyze script loaded. Use window.analyzeStudentDuplicates() to find duplicate students.');
  });

  // Import clear graduated class IDs script
  import('./utils/browserScripts/clearGraduatedClassIds').then(() => {
    console.log(' Clear graduated classIds script loaded. Use window.clearGraduatedClassIds() to fix graduated students.');
  });

  // Import access code reactivation utilities
  import('./utils/browserScripts/reactivateAccessCode').then((module) => {
    window.reactivateAccessCode = module.reactivateAccessCode;
    window.checkAccessCodeStatus = module.checkAccessCodeStatus;
    console.log(' Access code utilities loaded!');
    console.log('  - await window.reactivateAccessCode("CODE") - Reactivate and extend expiry to 30 days');
    console.log('  - await window.checkAccessCodeStatus("CODE") - Check code status without modifying');
  });

  // Emergency diagnostic for categories issue
  import('./scripts/emergencyDiagnostic').then(() => {
    console.log(' Emergency diagnostic loaded. Run: await window.emergencyDiagnostic()');
  });

  // Import debug parent link script
  import('./scripts/debugParentLink').then(module => {
    window.debugParentLink = module.debugParentLink;
    console.log(' Debug parent link script loaded. Usage: await window.debugParentLink("user-id")');
  });

  // Import fix parent links script
  import('./scripts/fixParentLinks').then(module => {
    window.fixParentLinks = module.fixParentLinks;
    console.log(' Parent links repair tool loaded. Usage: await window.fixParentLinks() for dry run, await window.fixParentLinks(false) to apply fixes');
  });

  import('./tests/rules/failure-recovery-browser-module').then((module) => {
    window.FailureRecoveryTestModule = module.default;
    window.runFailureRecoveryTest = async function() {
      const test = new module.default();
      return await test.runAllTests();
    };
  });

  // Load notification testing utilities
  import('./utils/testNotifications').then((module) => {
    window.testNotifications = module.default;
    console.log(' Notification test utilities loaded!');
    console.log('Usage:');
    console.log('  await testNotifications.createTestCriticalNotification(schoolId, userId)');
    console.log('  await testNotifications.createTestHighNotification(schoolId, userId)');
    console.log('  await testNotifications.createTestHighNotification(schoolId, userId)');
    console.log('  await testNotifications.createBulkTestNotifications(schoolId, userId, 5)');
  });

  // Load unified notification system test utilities
  import('./utils/testNotificationSystem').then(() => {
    console.log(' Unified notification system test utilities loaded!');
    console.log('Usage:');
    console.log('  await testNotificationSystem() - Test new notification system');
    console.log('  testToastSystem() - Test toast system');
    console.log('  cleanupTestNotifications() - Cleanup test data');
  });

  // Load preset message testing utilities
  import('./utils/testPresetMessages').then((module) => {
    window.testPresetMessages = module.default;
    console.log(' Preset message test utilities loaded!');
    console.log('Usage:');
    console.log('  await testPresetMessages.createTestAbsenceMessage(schoolId, userId)');
    console.log('  await testPresetMessages.createTestConsequenceMessage(schoolId, userId)');
    console.log('  await testPresetMessages.createBulkTestMessages(schoolId, userId, 3)');
  });

  // Load conversation testing utilities
  import('./utils/testConversations').then((module) => {
    window.testConversations = module.default;
    console.log(' Conversation test utilities loaded!');
    console.log('Usage:');
    console.log('  await testConversations.createTestStudentConversation(schoolId, userId, userName)');
    console.log('  await testConversations.createTestGeneralConversation(schoolId, userId, userName)');
    console.log('  await testConversations.createBulkTestConversations(schoolId, userId, userName, 5)');
    console.log('  await testConversations.sendTestMessage(conversationId, userId, userName)');
  });

  // Load loading timeout test utilities
  import('./utils/testLoadingTimeouts').then(() => {
    console.log(' Loading timeout test utilities loaded! Run with: window.testLoadingTimeouts()');
  });

  // Load Firestore access diagnostic tool
  import('./utils/firestoreAccessDiagnostic').then(() => {
    console.log(' Firestore diagnostic tool loaded! Run with: window.diagnoseFirestoreAccess()');
  });

  // Load loading pattern analyzer
  import('./utils/loadingPatternAnalyzer').then(() => {
    console.log(' Loading pattern analyzer loaded! Run with: window.analyzeAndFixLoadingPatterns()');
  });

  // DISABLED - All remaining script imports moved to on-demand loading
  // Available tools: window.loadDevTools("category")
  // Categories include: migration, testing, debug, cache, seeding, validation, student, class-fix

  // DISABLED - All testing and teams scripts moved to on-demand loading
  // Available via: window.loadDevTools("testing"), window.loadDevTools("teams")

  // DISABLED - Teams and diagnostic tools moved to on-demand loading
  // Use: window.loadDevTools("teams") or window.loadDevTools("diagnostic")
}

// Lazy load components for better performance
const Landing = lazy(() => import('./features/dashboard/components/Landing'));
const MarketingRoadmap = lazy(() => import('./features/dashboard/components/MarketingRoadmap'));
const MarketingJourney = lazy(() => import('./features/dashboard/components/MarketingJourney'));
const Pricing = lazy(() => import('./features/dashboard/components/Pricing'));
const BillingSuccess = lazy(() => import('./features/subscription/components/BillingSuccess'));
const BuyBasic = lazy(() => import('./features/buy/components/BuyBasic'));
const BuyPro = lazy(() => import('./features/buy/components/BuyPro'));
const BuySuccess = lazy(() => import('./features/buy/components/BuySuccess'));
const BuyCancel = lazy(() => import('./features/buy/components/BuyCancel'));
const Login = lazy(() => import('./features/auth/components/Login'));
const LoginJoin = lazy(() => import('./features/auth/components/LoginJoin'));
const Register = lazy(() => import('./features/auth/components/Register'));
const SetupAccount = lazy(() => import('./features/auth/components/SetupAccount'));
const ForgotPassword = lazy(() => import('./features/auth/components/ForgotPassword'));
const JoinSchool = lazy(() => import('./features/auth/components/JoinSchool'));
const JoinBulkInvitation = lazy(() => import('./features/auth/components/JoinBulkInvitation'));
const BulkInvitationManager = lazy(() => import('./components/admin/BulkInvitationManager'));
const TeacherDashboard = lazy(() => import('./features/dashboard/components/TeacherDashboard'));
const AdminDashboard = lazy(() => import('./features/dashboard/components/AdminDashboard'));
const DevDashboard = lazy(() => import('./features/dashboard/components/DevDashboard'));
const ClassesManager = lazy(() => import('./features/classes/components/ClassesManager'));
const StudentsManager = lazy(() => import('./features/students/components/StudentsManager'));
const PeoplePage = lazy(() => import('./features/people/PeoplePage'));
const StudentDashboard = lazy(() => import('./features/students/components/StudentDashboard'));
const TeacherPage = lazy(() => import('./features/staff/components/TeacherPage'));
const ClassList = lazy(() => import('./features/classes/components/ClassList'));
const SmartLists = lazy(() => import('./components/SmartLists'));
const SeatingManager = lazy(() => import('./features/seating/components/SeatingManager'));
const QuickIssueDashboard = lazy(() => import('./features/discipline/components/QuickIssueDashboardRefactored'));
const RecordsPage = lazy(() => import('./features/domains/pages/RecordsPage'));
const AwardPublicPage = lazy(() => import('./features/awards/components/AwardPublicPage'));
const StoryPublicPage = lazy(() => import('./features/stories/components/StoryPublicPage'));
const DisciplineQueue = lazy(() => import('./features/discipline/components/DisciplineQueueRefactored'));
const Settings = lazy(() => import('./components/Settings'));
const AdminTools = lazy(() => import('./pages/AdminTools'));
const Schools = lazy(() => import('./components/Schools'));
const SchoolProfile = lazy(() => import('./components/SchoolProfile'));
const RoleManagement = lazy(() => import('./features/auth/components/RoleManagement'));
const DevSettings = lazy(() => import('./components/DevSettings'));
const TestEnvironmentsPage = lazy(() => import('./pages/developer/TestEnvironmentsPage'));
const SigningDemo = lazy(() => import('./features/signing/dev/SigningDemo'));
const FirebaseUsage = lazy(() => import('./components/FirebaseUsage'));
const ParentChildLinking = lazy(() => import('./components/parentLinking/ParentChildLinkingSimple'));
const ParentChildLinkingManual = lazy(() => import('./components/parentLinking/ParentChildLinking'));
const AccessCodeManager = lazy(() => import('./components/parentLinking/AccessCodeManager'));
const CompleteProfile = lazy(() => import('./components/parentLinking/CompleteProfile'));
const MyLinkedChildren = lazy(() => import('./components/parentLinking/MyLinkedChildren'));
const DebugStudents = lazy(() => import('./components/DebugStudents'));


// New parent portal components
const WelcomePage = lazy(() => import('./components/parentOnboarding/WelcomePage'));
const OnboardingWizard = lazy(() => import('./components/parentOnboarding/OnboardingWizard'));
const SchoolOnboardingWizard = lazy(() => import('./features/onboarding/SchoolOnboardingWizard'));
// Tours temporarily disabled — preserved in src/features/tour for later polish.
// const FeatureTourPage = lazy(() => import('./features/tour/FeatureTourPage'));
const RemindersPage = lazy(() => import('./features/reminders/RemindersPage'));
const SchoolUpdatesPage = lazy(() => import('./features/updates/SchoolUpdatesPage'));
const ParentDashboard = lazy(() => import('./components/parentDashboard/ParentDashboard'));
const PermanentAgreements = lazy(() => import('./features/agreements/components/PermanentAgreements'));
const AgreementsAdmin = lazy(() => import('./features/agreements/components/AgreementsAdmin'));
const PoliciesAdminPage = lazy(() => import('./features/policies/pages/PoliciesAdminPage'));
const PolicyLibraryPage = lazy(() => import('./features/policies/pages/PolicyLibraryPage'));
const ClassroomDisplay = lazy(() => import('./components/classroomDisplay/ClassroomDisplay'));
const ClassDisplayPage = lazy(() => import('./features/classDisplay/components/ClassDisplayPage'));
const ClassDisplayRemote = lazy(() => import('./features/classDisplay/components/ClassDisplayRemote'));

// Parent child pages
const ChildSheetPage = lazy(() => import('./features/students/components/parent/ChildSheetPage'));
const ChildBehaviourPage = lazy(() => import('./features/students/components/parent/ChildBehaviourPage'));
const ChildRecognitionPage = lazy(() => import('./features/students/components/parent/ChildRecognitionPage'));
const ChildIncidentsPage = lazy(() => import('./features/students/components/parent/ChildIncidentsPage'));
const MessagesPage = lazy(() => import('./features/messaging/pages/MessagesPage'));
const SmartMessagesPage = lazy(() => import('./features/messaging/pages/SmartMessagesPage'));
const SmartMessageTrackerDetail = lazy(() => import('./features/messaging/components/SmartMessageTrackerDetail'));
const SmartMessageFormView = lazy(() => import('./features/messaging/components/SmartMessageFormView'));
const MessageHistory = lazy(() => import('./pages/MessageHistory'));
// ConversationsPage removed — messenger now lives in the slide-out chat panel (FAB)
const MessageComposer = lazy(() => import('./features/messaging/components/MessageComposer'));
const DomainsPage = lazy(() => import('./features/domains/pages/DomainsPage'));
const UsersPage = lazy(() => import('./features/admin/components/UsersPage'));
const MessageTypeSettings = lazy(() => import('./features/admin/components/MessageTypeSettings'));
const NotificationList = lazy(() => import('./features/notifications/components/NotificationList'));
const ProjectControlPage = import.meta.env.DEV
  ? lazy(() => import('./features/projectControl/ProjectControlPage'))
  : null;
const TeamsPage = lazy(() => import('./features/teams/TeamsPage'));
const RolesPage = lazy(() => import('./features/roles/RolesPage'));
const OrgChartPage = lazy(() => import('./features/orgChart/OrgChartPage'));
const SchoolPeriods = lazy(() => import('./components/schoolPeriods/SchoolPeriods'));
const DayBuilder = lazy(() => import('./features/timetable/components/DayBuilder'));
const ScheduleBuilder = lazy(() => import('./features/timetable/components/ScheduleBuilder'));
const TimetableBuilder = lazy(() => import('./features/timetable/components/TimetableBuilder'));
const TimetableViewer = lazy(() => import('./features/timetable/components/TimetableViewer'));
const SubjectsManager = lazy(() => import('./features/timetable/components/SubjectsManager'));
const GradesManager = lazy(() => import('./features/grades/components/GradesManager'));
// TimetableAssignmentBuilder removed — draft mode is in TimetableBuilder
const WelcomeLetters = lazy(() => import('./features/welcomeLetters/WelcomeLettersPage'));
const DateRangeManager = lazy(() => import('./features/discipline/components/DateRangeManager'));
const AnnouncementsManager = lazy(() => import('./pages/AnnouncementsManager'));
const CampaignsManager = lazy(() => import('./features/campaigns/components/CampaignsManager'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const SchoolProfilePage = lazy(() => import('./features/school/components/SchoolProfilePage'));
const CalendarPage = lazy(() => import('./features/calendar/components/CalendarPage'));
const TodoPage = lazy(() => import('./features/todos/components/TodoPage'));
const TaskPage = lazy(() => import('./features/tasks/components/TaskPage'));
const WorkflowTemplatesPage = lazy(() => import('./features/workflows/components/WorkflowTemplatesPage'));
const WorkflowTemplateBuilder = lazy(() => import('./features/workflows/components/WorkflowTemplateBuilder'));
const WorkflowInstancesPage = lazy(() => import('./features/workflows/components/WorkflowInstancesPage'));

// Setup pages (Issue #129 — settings-style consolidation)
const SetupHubPage = lazy(() => import('./pages/setup/SetupHubPage'));
const SchoolConfigurationPage = lazy(() => import('./pages/setup/SchoolConfigurationPage'));

// Skeleton components for loading states
const StudentDashboardSkeleton = lazy(() => import('./features/students/components/StudentDashboardSkeleton'));
const TeacherPageSkeleton = lazy(() => import('./features/staff/components/TeacherPageSkeleton'));
const DisciplineQueueSkeleton = lazy(() => import('./components/skeletons/DisciplineQueueSkeleton'));
const ClassesManagerSkeleton = lazy(() => import('./features/classes/components/ClassesManagerSkeleton'));

// Discipline Dashboard - RE-ENABLED with caching optimizations
const DisciplineDashboard = lazy(() => import('./components/dashboard/DisciplineDashboard'));

// Access ID feature
const ManualLogEntry = lazy(() => import('./features/accessId/components/ManualLogEntry'));

// Protected Route Component
const ProtectedRoute = ({ children, loadingSkeleton }) => {
  const { user, initializing } = useAuth();

  if (initializing) {
    // If route provides a skeleton, use it
    if (loadingSkeleton) {
      return loadingSkeleton;
    }

    // Otherwise, fall back to spinner
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return user ? children : <Navigate to={ROUTES.LOGIN} />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, initializing, signingUp } = useAuth();
  const { currentRole, loading: roleLoading } = useRoleSwitcher();

  // Wait for auth init and role resolution
  if (initializing || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // signingUp = auth in progress, user doc still being written.
  // Show login page (children) behind the Google popup — don't redirect yet.
  if (signingUp) {
    return children;
  }

  if (user) {
    // Check for pending redirect (e.g., parent linking after registration)
    // With signingUp preventing early redirect, the Router key is stable by
    // the time we get here, so <Navigate> works correctly.
    const pendingRedirect = sessionStorage.getItem('pendingAuthRedirect');
    if (pendingRedirect) {
      sessionStorage.removeItem('pendingAuthRedirect');
      return <Navigate to={pendingRedirect} replace />;
    }

    return <Navigate to={dashboardPathForRole(currentRole)} />;
  }

  return children;
};

// Protected Parent Route Component - checks auth and parent role only
// Child pages handle studentId validation using StandardPage notFound pattern
const ProtectedParentRoute = ({ children, loadingSkeleton }) => {
  const { user, initializing } = useAuth();
  const { currentRole } = useRoleSwitcher();
  const { linkedChildren, hasLoadedChildren } = useParentStore();

  // Loading state
  if (initializing) {
    if (loadingSkeleton) {
      return loadingSkeleton;
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  // Redirect to teacher dashboard if not parent role
  if (currentRole !== 'parent') {
    return <Navigate to={ROUTES.TEACHER_DASHBOARD} replace />;
  }

  if (shouldRedirectUnlinkedParent({
    currentRole,
    linkedChildren,
    hasLoadedChildren,
  })) {
    return <Navigate to={ROUTES.PARENT_LINKING} replace />;
  }

  return children;
};

// Gmail OAuth result page — auto-closes popup or shows status
const GmailOAuthResult = ({ success }) => {
  React.useEffect(() => {
    // If opened as popup, close it after a short delay
    const timer = setTimeout(() => {
      if (window.opener) {
        window.close();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="card bg-base-100 shadow-lg p-8 text-center max-w-sm">
        {success ? (
          <>
            <div className="text-4xl mb-4">&#10003;</div>
            <h2 className="text-lg font-semibold mb-2">Gmail Connected</h2>
            <p className="text-base-content/60 text-sm">You can close this window and return to Swan.</p>
          </>
        ) : (
          <>
            <div className="text-4xl mb-4">&#10007;</div>
            <h2 className="text-lg font-semibold mb-2">Connection Failed</h2>
            <p className="text-base-content/60 text-sm">Something went wrong. Please try connecting Gmail again from the Email tab.</p>
          </>
        )}
      </div>
    </div>
  );
};

// Workspace OAuth result page — auto-closes popup or shows status
const WorkspaceOAuthResult = ({ success }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (window.opener) {
        window.close();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="card bg-base-100 shadow-lg p-8 text-center max-w-sm">
        {success ? (
          <>
            <div data-testid="success-icon" className="text-4xl mb-4">&#10003;</div>
            <h2 className="text-lg font-semibold mb-2">Workspace Connected</h2>
            <p className="text-base-content/60 text-sm">You can close this window and return to Swan.</p>
          </>
        ) : (
          <>
            <div data-testid="error-icon" className="text-4xl mb-4">&#10007;</div>
            <h2 className="text-lg font-semibold mb-2">Connection Failed</h2>
            <p className="text-base-content/60 text-sm">Something went wrong. Please try again from Settings.</p>
          </>
        )}
      </div>
    </div>
  );
};

// Routes that should NOT have menus (public pages)
const PUBLIC_ROUTES_WITHOUT_MENUS = [
  ROUTES.HOME,
  ROUTES.ROADMAP,
  ROUTES.JOURNEY,
  ROUTES.PRICING,
  ROUTES.BILLING_SUCCESS,
  ROUTES.BUY_BASIC,
  ROUTES.BUY_PRO,
  ROUTES.BUY_SUCCESS,
  ROUTES.BUY_CANCEL,
  ROUTES.LOGIN,
  '/login-join', // Login page for joining schools
  ROUTES.REGISTER,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.WELCOME,
  '/join', // School invitation page (can be accessed before login)
  '/gmail-connected',
  '/gmail-error',
  '/workspace-connected',
  '/workspace-error',
  ROUTES.CLASS_DISPLAY,
  ROUTES.CLASS_DISPLAY_REMOTE,
  ROUTES.CLASSROOM_DISPLAY,
];

// Main App Layout (with menus)
const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { currentRole, getRoleDisplayName, canSwitchRoles, availableRoles, switchRole, currentPermissions, userPermissions } = useRoleSwitcher();
  const { menuItems } = useMenuItemsContext();
  const {
    schoolId,
    school,
    developerSchoolId,
    selectDeveloperSchool,
  } = useSchool();
  const location = useLocation();
  const navigate = useNavigate();
  const activeSubstratePanel = useSubstratePanelStore((state) => state.activePanel);
  const hasDesktopReferencePanel = currentRole !== 'parent' && activeSubstratePanel != null;
  const previouslyHadDesktopReferencePanel = React.useRef(hasDesktopReferencePanel);

  // Opening the reference panel changes the Queue from document scrolling to
  // a bounded main-column scroller. Move the current position between those
  // two owners so a stale scrollTop from an earlier panel session cannot clip
  // the top of the page, while a user who deliberately scrolled keeps their
  // place beside the contextual profile.
  React.useLayoutEffect(() => {
    const hadPanel = previouslyHadDesktopReferencePanel.current;
    if (hadPanel === hasDesktopReferencePanel) return;

    const main = document.querySelector('[data-testid="page-shell"]');
    if (!main) return;

    if (hasDesktopReferencePanel) {
      const documentScrollTop = window.scrollY;
      main.scrollTop = documentScrollTop;
      window.scrollTo(0, 0);
    } else {
      const mainScrollTop = main.scrollTop;
      main.scrollTop = 0;
      window.scrollTo(0, mainScrollTop);
    }

    previouslyHadDesktopReferencePanel.current = hasDesktopReferencePanel;
  }, [hasDesktopReferencePanel]);

  // Check if we're in fullscreen mode via URL parameter
  const isFullscreenMode = new URLSearchParams(location.search).get('fullscreen') === 'true';
  const isDeveloperWorkspace = isDeveloperWorkspaceAvailable(
    import.meta.env.VITE_FIREBASE_PROJECT_ID,
  ) && (
    location.pathname === ROUTES.DEV_DASHBOARD
    || location.pathname.startsWith('/developer/')
  );

  // PART 2: Check for pending invitation after login (handshake trigger)
  React.useEffect(() => {
    if (user && location.pathname !== '/join') {
      const pendingInvitation = sessionStorage.getItem('pendingInvitation');
      if (pendingInvitation) {
        try {
          const { schoolId, inviteId } = JSON.parse(pendingInvitation);
          console.log('[App] Found pending invitation, redirecting to /join:', { schoolId, inviteId });
          // Don't clear sessionStorage here - let JoinSchool handle that
          navigate(`/join?school=${schoolId}&invite=${inviteId}`);
        } catch (error) {
          console.error('[App] Error parsing pending invitation:', error);
          sessionStorage.removeItem('pendingInvitation');
        }
      }
    }
  }, [user, location.pathname, navigate]);

  // Suppress drawer transitions during viewport resize to prevent dark flash
  // when crossing the lg breakpoint (sidebar show/hide)
  React.useEffect(() => {
    let timeout;
    const handleResize = () => {
      document.documentElement.classList.add('resize-no-transition');
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        document.documentElement.classList.remove('resize-no-transition');
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  // Check if current route should have menus
  const shouldShowMenus = !PUBLIC_ROUTES_WITHOUT_MENUS.includes(location.pathname) && !location.pathname.startsWith('/award/') && !location.pathname.startsWith('/j/') && !isFullscreenMode;

  // Public pages without menus OR fullscreen mode - just render children
  // DomainProvider still wraps so class-display / other fullscreen routes can
  // read the active domain registry.
  if (!shouldShowMenus) {
    return <DomainProvider schoolId={schoolId}>{children}</DomainProvider>;
  }

  if (isDeveloperWorkspace) {
    return (
      <DeveloperWorkspaceShell
        authorized={Boolean(userPermissions?.dev || currentPermissions?.dev)}
        user={user}
        onLogout={logout}
      >
        {children}
      </DeveloperWorkspaceShell>
    );
  }

  // Protected pages with menus
  return (
    <DomainProvider schoolId={schoolId}>
    <div className="min-h-screen bg-base-200 pt-20">
      <NavBar
        user={user}
        onLogout={logout}
        currentRole={currentRole}
        getRoleDisplayName={getRoleDisplayName}
        canSwitchRoles={canSwitchRoles()}
        availableRoles={availableRoles}
        switchRole={switchRole}
        currentPermissions={currentPermissions}
        menuItems={menuItems}
      />
      <CampaignBanner />
      <AnnouncementBanner />
      {developerSchoolId && (
        <div className="flex flex-wrap items-center justify-center gap-3 border-b border-info/30 bg-info/10 px-4 py-2 text-sm">
          <span>
            <strong>Developer inspection:</strong> {school?.name || developerSchoolId}
            {' · '}no school membership changed
          </span>
          <button
            className="btn btn-ghost btn-xs"
            type="button"
            onClick={async () => {
              await selectDeveloperSchool(null);
              navigate(ROUTES.DEV_TEST_ENVIRONMENTS);
            }}
          >
            Return to Developer Workspace
          </button>
        </div>
      )}
      <div
        data-testid="workspace-columns"
        data-reference-panel-open={hasDesktopReferencePanel ? 'true' : 'false'}
        className={`flex ${
          hasDesktopReferencePanel
            ? 'min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] lg:min-h-0 lg:overflow-hidden'
            : 'min-h-[calc(100vh-5rem)]'
        }`}
      >
        <div
          data-testid="workspace-main-column"
          className={`flex-1 min-w-0 ${hasDesktopReferencePanel ? 'lg:flex lg:min-h-0 lg:flex-col' : ''}`}
        >
          {currentRole !== 'parent' && !location.pathname.startsWith('/setup') && (
            <PersistentContextBar containedDesktop={hasDesktopReferencePanel} />
          )}
          <PageShell className={hasDesktopReferencePanel
            ? 'lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain'
            : ''}
          >
            {children}
          </PageShell>
        </div>
        {currentRole !== 'parent' && <SubstratePanel />}
        {currentRole !== 'parent' && <SubstrateRail />}
      </div>
      {user && (
        <>
          {/* Mobile bottom navigation dock */}
          <MobileDock menuItems={menuItems} navigate={navigate} location={location} />
          {/* Chat panel (AI + People) — all staff roles */}
          {currentRole !== 'parent' && (
            <>
              <AgentChatButton />
              <AgentChat />
            </>
          )}
          {/* Global school invitation modal - shows when admin approves teacher's access request */}
          <SchoolInvitationModal />
          {/* Global session footer — persists across navigation when a session is active */}
          <GlobalSessionFooter />
        </>
      )}
    </div>
    </DomainProvider>
  );
};

// Debug: log catch-all redirects to find what's eating /workflow-templates
const CatchAllRedirect = () => {
  const loc = window.location.pathname;
  console.error(`[CatchAll] ️ No route matched "${loc}" — redirecting to HOME`);
  return <Navigate to={ROUTES.HOME} />;
};

const LegacyDomainEditorRedirect = ({ domain, step, extraParams, pathParam, queryParam }) => {
  const location = useLocation();
  const routeParams = useParams();
  const pathValue = pathParam ? routeParams[pathParam] : null;
  const resolvedExtraParams = pathValue && queryParam
    ? { ...(extraParams || {}), [queryParam]: pathValue }
    : extraParams;
  return (
    <Navigate
      to={buildDomainEditorDestination(location, { domain, step, extraParams: resolvedExtraParams })}
      replace
    />
  );
};

const LegacySetupDomainsRedirect = () => {
  const location = useLocation();
  return <Navigate to={buildDomainEditorDestination(location, {})} replace />;
};

const LegacySetupBehaviourRedirect = () => {
  const location = useLocation();
  return <Navigate to={setupBehaviourDestination(location)} replace />;
};

// AppRouterWrapper - Role changes are handled by switchRole() clearing sessionStorage caches
// REMOVED key={currentRole} which was causing full app remount on every role init transition
const AppRouterWrapper = () => {
  const { currentRole } = useRoleSwitcher();

  // Debug: track role transitions without remounting
  useEffect(() => {
    console.log(`[AppRouterWrapper]  currentRole changed to: ${currentRole}`);
  }, [currentRole]);

  return (
    <Router>
      <AppLayout>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}>
          <RoleBasedRoute>
            <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.HOME} element={<PublicRoute><Landing /></PublicRoute>} />
              <Route path={ROUTES.ROADMAP} element={<MarketingRoadmap />} />
              <Route path={ROUTES.JOURNEY} element={<MarketingJourney />} />
              <Route path={ROUTES.PRICING} element={<Pricing />} />
              <Route path={ROUTES.BILLING_SUCCESS} element={<BillingSuccess />} />
              <Route path={ROUTES.BUY_BASIC} element={<BuyBasic />} />
              <Route path={ROUTES.BUY_PRO} element={<BuyPro />} />
              <Route path={ROUTES.BUY_SUCCESS} element={<BuySuccess />} />
              <Route path={ROUTES.BUY_CANCEL} element={<BuyCancel />} />
              <Route path={ROUTES.LOGIN} element={<PublicRoute><Login /></PublicRoute>} />
              {/* LoginJoin is NOT wrapped in PublicRoute - it's a dumb courier that just shows login */}
              <Route path="/login-join" element={<LoginJoin />} />
              <Route path={ROUTES.REGISTER} element={<PublicRoute><Register /></PublicRoute>} />
              <Route path={ROUTES.FORGOT_PASSWORD} element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path={ROUTES.WELCOME} element={<WelcomePage />} />
              {/* SetupAccount - post-signup role selection for general signups */}
              <Route path="/setup-account" element={<SetupAccount />} />
              {/* JoinSchool handles the actual handshake after login */}
              <Route path="/join" element={<JoinSchool />} />
              {/* JoinBulkInvitation handles bulk invitation codes */}
              <Route path="/join-bulk" element={<JoinBulkInvitation />} />

              {/* Public Award Page - No auth required */}
              <Route path="/award/:shareToken" element={<AwardPublicPage />} />

              {/* Public Story share page - No auth required */}
              <Route path="/j/:shareId" element={<StoryPublicPage />} />

              {/* Gmail OAuth callback routes */}
              <Route path="/gmail-connected" element={<GmailOAuthResult success />} />
              <Route path="/gmail-error" element={<GmailOAuthResult />} />

              {/* Workspace OAuth callback routes */}
              <Route path="/workspace-connected" element={<WorkspaceOAuthResult success />} />
              <Route path="/workspace-error" element={<WorkspaceOAuthResult />} />

              {/* Protected Routes */}
              <Route path={ROUTES.TEACHER_DASHBOARD} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><TeacherDashboard /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.ADMIN_DASHBOARD} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><AdminDashboard /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.DEV_DASHBOARD} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><DevDashboard /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path="/dev-dashboard" element={<Navigate to={ROUTES.DEV_DASHBOARD} replace />} />
              <Route path={ROUTES.CLASSES} element={<LoadingOrchestrator skeleton={<ClassesManagerSkeleton />}><ProtectedRoute><ClassesManager /></ProtectedRoute></LoadingOrchestrator>} />
              {/* Phase 1 (menu/IA migration): /students redirects into the People page directory lens. */}
              <Route path={ROUTES.STUDENTS} element={<Navigate to="/people?lens=directory&cohort=student" replace />} />
              <Route path={ROUTES.PEOPLE} element={<ProtectedRoute><PeoplePage /></ProtectedRoute>} />
              <Route path={`${ROUTES.STUDENT_PROFILE}/:studentId`} element={<ProtectedRoute loadingSkeleton={<StudentDashboardSkeleton />}><StudentDashboard /></ProtectedRoute>} />
              <Route path={`${ROUTES.TEACHER_PROFILE}/:teacherId`} element={<ProtectedRoute loadingSkeleton={<TeacherPageSkeleton />}><TeacherPage /></ProtectedRoute>} />
              <Route path="/mychildren" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><MyLinkedChildren /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path="/mychildren/:studentId" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedParentRoute><ChildSheetPage /></ProtectedParentRoute></LoadingOrchestrator>} />
              <Route path="/mychildren/:studentId/behaviour" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedParentRoute><ChildBehaviourPage /></ProtectedParentRoute></LoadingOrchestrator>} />
              <Route path="/mychildren/:studentId/recognition" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedParentRoute><ChildRecognitionPage /></ProtectedParentRoute></LoadingOrchestrator>} />
              <Route path="/mychildren/:studentId/incidents" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedParentRoute><ChildIncidentsPage /></ProtectedParentRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.CLASSLIST} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><ClassList /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.SMARTLISTS} element={<LoadingOrchestrator skeleton={<SmartListsSkeleton />}><ProtectedRoute><SmartLists /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.SEATING} element={<LoadingOrchestrator skeleton={<SeatingManagerSkeleton />}><ProtectedRoute><SeatingManager /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.QUICK_ISSUE} element={<LoadingOrchestrator skeleton={<QuickIssueSkeleton />}><ProtectedRoute><QuickIssueDashboard /></ProtectedRoute></LoadingOrchestrator>} />
              {LEGACY_DOMAIN_EDITOR_ROUTES.map(({
                path,
                domain,
                step,
                extraParams,
                pathParam,
                queryParam,
              }) => (
                <Route
                  key={path}
                  path={path}
                  element={(
                    <LegacyDomainEditorRedirect
                      domain={domain}
                      step={step}
                      extraParams={extraParams}
                      pathParam={pathParam}
                      queryParam={queryParam}
                    />
                  )}
                />
              ))}
              <Route path={ROUTES.RECORDS} element={<LoadingOrchestrator skeleton={<DisciplineQueueSkeleton />}><ProtectedRoute><RecordsPage /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.DISCIPLINE_QUEUE} element={<LoadingOrchestrator skeleton={<DisciplineQueueSkeleton />}><ProtectedRoute><DisciplineQueue /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path="/queue" element={<Navigate to={ROUTES.DISCIPLINE_QUEUE} replace />} />
              <Route path="/discipline-dashboard" element={<ProtectedRoute><DisciplineDashboard /></ProtectedRoute>} />
              <Route path={ROUTES.SETTINGS} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><Settings /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={`${ROUTES.SETTINGS}/:section`} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><Settings /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.ADMIN_TOOLS} element={<ProtectedRoute><AdminTools /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOLS} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><Schools /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={`${ROUTES.SCHOOLS}/:id`} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><SchoolProfile /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.ROLE_MANAGEMENT} element={<ProtectedRoute><RoleManagement /></ProtectedRoute>} />
              <Route path="/admin/invitations" element={<ProtectedRoute><BulkInvitationManager /></ProtectedRoute>} />
              <Route path={ROUTES.DEV_SETTINGS} element={<ProtectedRoute><DevSettings /></ProtectedRoute>} />
              <Route
                path={ROUTES.DEV_TEST_ENVIRONMENTS}
                element={isDeveloperWorkspaceAvailable(import.meta.env.VITE_FIREBASE_PROJECT_ID)
                  ? <ProtectedRoute><TestEnvironmentsPage /></ProtectedRoute>
                  : <Navigate to={ROUTES.DEV_DASHBOARD} replace />}
              />
              {import.meta.env.DEV && (
                <Route path={ROUTES.PROJECT_CONTROL} element={<ProtectedRoute><ProjectControlPage /></ProtectedRoute>} />
              )}
              {import.meta.env.DEV && (
                <Route path="/project-control" element={<Navigate to={ROUTES.PROJECT_CONTROL} replace />} />
              )}
              <Route path="/dev/signing-demo" element={<ProtectedRoute><SigningDemo /></ProtectedRoute>} />
              <Route path={ROUTES.FIREBASE_USAGE} element={<ProtectedRoute><FirebaseUsage /></ProtectedRoute>} />
              <Route path={ROUTES.PARENT_LINKING} element={<LoadingOrchestrator skeleton={<LinkingPageSkeleton />}><ParentChildLinking /></LoadingOrchestrator>} />
              <Route path="/parent-linking-manual" element={<ProtectedRoute><ParentChildLinkingManual /></ProtectedRoute>} />
              <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
              <Route path={ROUTES.ACCESS_CODES} element={<ProtectedRoute><AccessCodeManager /></ProtectedRoute>} />
              <Route path="/my-children" element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><MyLinkedChildren /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path="/debug-students" element={<ProtectedRoute><DebugStudents /></ProtectedRoute>} />

              <Route path={ROUTES.ONBOARDING} element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><OnboardingWizard /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.SCHOOL_ONBOARDING} element={<ProtectedRoute><SchoolOnboardingWizard /></ProtectedRoute>} />
              {/* Tours disabled — route preserved in constants for later polish. */}
              {/* <Route path={ROUTES.TOUR} element={<ProtectedRoute><FeatureTourPage /></ProtectedRoute>} /> */}
              <Route path={ROUTES.REMINDERS} element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOL_UPDATES} element={<ProtectedRoute><SchoolUpdatesPage /></ProtectedRoute>} />
              <Route path={ROUTES.PARENT_DASHBOARD} element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><ParentDashboard /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.AGREEMENTS} element={<ProtectedRoute><PermanentAgreements /></ProtectedRoute>} />
              <Route path={ROUTES.AGREEMENTS_ADMIN} element={<ProtectedRoute><AgreementsAdmin /></ProtectedRoute>} />
              <Route path={ROUTES.POLICIES_ADMIN} element={<ProtectedRoute><PoliciesAdminPage /></ProtectedRoute>} />
              <Route path={ROUTES.POLICIES} element={<ProtectedRoute><PolicyLibraryPage /></ProtectedRoute>} />
              <Route path={ROUTES.CLASSROOM_DISPLAY} element={<ClassroomDisplay />} />
              <Route path={ROUTES.CLASS_DISPLAY_REMOTE} element={<ProtectedRoute><ClassDisplayRemote /></ProtectedRoute>} />
              <Route path={ROUTES.CLASS_DISPLAY} element={<ProtectedRoute><ClassDisplayPage /></ProtectedRoute>} />
              <Route path={ROUTES.MESSAGES} element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><MessagesPage /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.MESSAGE_HISTORY} element={<LoadingOrchestrator skeleton={<StudentDashboardSkeleton />}><ProtectedRoute><MessageHistory /></ProtectedRoute></LoadingOrchestrator>} />
              {/* Smart Messages — teacher-side tracker */}
              <Route path={ROUTES.SMART_MESSAGES} element={<ProtectedRoute><SmartMessagesPage /></ProtectedRoute>} />
              <Route path="/messaging/smart-messages" element={<Navigate to={ROUTES.SMART_MESSAGES} replace />} />
              <Route path="/messaging/smart-messages/:id" element={<ProtectedRoute><SmartMessageTrackerDetail /></ProtectedRoute>} />
              {/* Smart Message full-page response form — email links land here; self-guards auth via pendingAuthRedirect */}
              <Route path="/smart-message-form/:schoolId/:smartMessageId" element={<SmartMessageFormView />} />
              {/* Conversations now live in the slide-out chat panel (FAB) */}
              <Route path={ROUTES.CONVERSATIONS} element={<Navigate to={ROUTES.HOME} />} />
              <Route path="/compose-message" element={<ProtectedRoute><MessageComposer /></ProtectedRoute>} />
              <Route path={ROUTES.DOMAINS} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><DomainsPage /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path={ROUTES.USERS} element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOL_NOTIFICATION_SETTINGS} element={<LoadingOrchestrator skeleton={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="large" /></div>}><ProtectedRoute><MessageTypeSettings /></ProtectedRoute></LoadingOrchestrator>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationList /></ProtectedRoute>} />
              {/* Phase 1 (menu/IA migration): /teams and /roles redirect into the People page groups lens. */}
              <Route path="/teams" element={<Navigate to="/people?lens=groups" replace />} />
              <Route path="/roles" element={<Navigate to="/people?lens=groups" replace />} />
              <Route path="/org-chart" element={<ProtectedRoute><OrgChartPage /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOL_PERIODS} element={<ProtectedRoute><SchoolPeriods /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOL_PROFILE} element={<ProtectedRoute><SchoolProfilePage /></ProtectedRoute>} />
              <Route path={ROUTES.TIMETABLE} element={
                <ProtectedRoute><TimetableBuilder /></ProtectedRoute>
              } />
              <Route path={ROUTES.DAY_BUILDER} element={
                <ProtectedRoute><DayBuilder /></ProtectedRoute>
              } />
              <Route path={ROUTES.CYCLE_BUILDER} element={
                <ProtectedRoute><ScheduleBuilder /></ProtectedRoute>
              } />
              <Route path={ROUTES.TIMETABLE_BUILDER} element={
                <ProtectedRoute><TimetableBuilder /></ProtectedRoute>
              } />
              <Route path={ROUTES.SUBJECTS} element={<ProtectedRoute><SubjectsManager /></ProtectedRoute>} />
              <Route path={ROUTES.GRADES} element={<ProtectedRoute><GradesManager /></ProtectedRoute>} />
              {/* /timetable-assignments removed — use /timetable-builder instead */}
              <Route path={ROUTES.WELCOME_LETTERS} element={<ProtectedRoute><WelcomeLetters /></ProtectedRoute>} />
              <Route path={ROUTES.ACADEMIC_CALENDAR} element={<Navigate to={ROUTES.DATE_RANGES} replace />} />
              <Route path={ROUTES.DATE_RANGES} element={<ProtectedRoute><DateRangeManager /></ProtectedRoute>} />
              <Route path="/announcements" element={<ProtectedRoute><AnnouncementsManager /></ProtectedRoute>} />
              <Route path={ROUTES.CAMPAIGNS} element={<ProtectedRoute><CampaignsManager /></ProtectedRoute>} />
              <Route path={ROUTES.HELP_CENTER} element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
              <Route path={ROUTES.CALENDAR} element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
              <Route path={ROUTES.MY_TODOS} element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />
              <Route path={ROUTES.MY_TASKS} element={<ProtectedRoute><TaskPage /></ProtectedRoute>} />
              <Route path={ROUTES.WORKFLOW_TEMPLATES} element={<ProtectedRoute><WorkflowTemplatesPage /></ProtectedRoute>} />
              <Route path={`${ROUTES.WORKFLOW_TEMPLATES}/new`} element={<ProtectedRoute><WorkflowTemplateBuilder /></ProtectedRoute>} />
              <Route path={`${ROUTES.WORKFLOW_TEMPLATES}/:templateId`} element={<ProtectedRoute><WorkflowTemplateBuilder /></ProtectedRoute>} />
              <Route path={ROUTES.WORKFLOWS} element={<ProtectedRoute><WorkflowInstancesPage /></ProtectedRoute>} />

              {/* Access ID routes */}
              <Route path={ROUTES.ACCESS_LOG} element={<ProtectedRoute><ManualLogEntry /></ProtectedRoute>} />

              {/* Setup hub (Issue #129) — single consolidated /setup page */}
              <Route path={ROUTES.SETUP} element={<ProtectedRoute><SetupHubPage /></ProtectedRoute>} />
              <Route path={ROUTES.SCHOOL_CONFIGURATION} element={<ProtectedRoute><SchoolConfigurationPage /></ProtectedRoute>} />
              {/* Backwards-compat: old per-area URLs redirect into the hub */}
              <Route path={ROUTES.SETUP_PROFILE} element={<Navigate to="/setup?area=profile" replace />} />
              <Route path={ROUTES.SETUP_CALENDAR} element={<Navigate to="/setup?area=calendar" replace />} />
              <Route path={ROUTES.SETUP_ROSTER} element={<Navigate to="/setup?area=roster" replace />} />
              <Route path={ROUTES.SETUP_TIMETABLE} element={<Navigate to="/setup?area=timetable" replace />} />
              <Route path={ROUTES.SETUP_ORGANIZATION} element={<Navigate to="/setup?area=organization" replace />} />
              <Route path={ROUTES.SETUP_COMMUNICATIONS} element={<Navigate to="/setup?area=communications" replace />} />
              {/* Behaviour and Recognition are peer Domains. Old setup links
                  resolve into their corresponding Domain tab. */}
              <Route path={ROUTES.SETUP_BEHAVIOUR} element={<LegacySetupBehaviourRedirect />} />
              <Route path={ROUTES.SETUP_DOMAINS} element={<LegacySetupDomainsRedirect />} />

              {/* Catch all route */}
              <Route path="*" element={<CatchAllRedirect />} />
            </Routes>
          </RoleBasedRoute>
        </Suspense>
      </AppLayout>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <NotificationProvider>
            <PrivacyMaskProvider>
              <AuthProvider>
                <SchoolProvider>
                  <BrandingProvider>
                  <RoleSwitcherProvider>
                    <SchoolBrandingController />
                    <ScopeProvider>
                      <ContextModeProvider>
                        <MenuItemsProvider>
                          <AppRouterWrapper />
                          <EnvironmentBadge />
                          {/* <UpdateNotification /> — silent auto-update via SW, no toast needed */}
                          <VersionDebugPanel />
                        </MenuItemsProvider>
                      </ContextModeProvider>
                    </ScopeProvider>
                  </RoleSwitcherProvider>
                  </BrandingProvider>
                </SchoolProvider>
              </AuthProvider>
            </PrivacyMaskProvider>
          </NotificationProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
